import { useCallback, useEffect, useRef } from 'react';

import { Textfield, TextfieldType } from '@webapp/common/components/textfield';
import { formatPhoneOnlyNumbers } from '@webapp/common/lib/phone';

import css from './phone-input.css';

export interface PhoneValue {
    code: string;
    number: string;
}

interface PhoneCountry {
    code: string;
    numberMask: string;
}

const COUNTRIES: Array<PhoneCountry> = [
    { code: '7', numberMask: '(000) 000-00-00' },
    { code: '994', numberMask: '(00) 000-00-00' },
];

export const DEFAULT_PHONE: PhoneValue = { code: '+7', number: '' };

const CODE_MASK = '+000';

const imaskOptions = {
    lazy: true,
    unmask: false,
};

const getDigits = (value: string): string => value.replace(/\D/g, '');

const findCountry = (code: string): PhoneCountry | undefined =>
    COUNTRIES.find((country) => country.code === getDigits(code));

const getNumberMask = (code: string): string => (findCountry(code) || COUNTRIES[0]).numberMask;

export const isPhoneComplete = ({ code, number }: PhoneValue): boolean => {
    const country = findCountry(code);

    return Boolean(country) && getDigits(number).length === getDigits(country.numberMask).length;
};

export const getPhoneDigits = ({ code, number }: PhoneValue): string => formatPhoneOnlyNumbers(code + number);

interface Props {
    value: PhoneValue;
    onChange: (value: PhoneValue) => void;
    disabled?: boolean;
}

export const PhoneInput: FC<Props> = ({ value, onChange, disabled }) => {
    const { code, number } = value;
    const numberMask = getNumberMask(code);
    const numberRef = useRef(null);

    useEffect(() => {
        if (code.length === CODE_MASK.length && findCountry(code)) {
            numberRef.current?.focus();
        }
    }, [code]);

    const handleCodeChange = useCallback(
        (newCode: string) => {
            const isMaskChanged = getNumberMask(newCode) !== numberMask;

            onChange({ code: newCode, number: isMaskChanged ? '' : number });
        },
        [number, numberMask, onChange],
    );

    const handleNumberChange = useCallback(
        (newNumber: string) => {
            onChange({ code, number: newNumber });
        },
        [code, onChange],
    );

    return (
        <div className={css.fields}>
            <Textfield
                clearable
                transparent
                className={css.codeControl}
                defaultValue={code}
                disabled={disabled}
                imaskOptions={imaskOptions}
                mask={CODE_MASK}
                onFocus={(e) => e.currentTarget.select()}
                placeholder={CODE_MASK}
                rootClassName={css.codeRoot}
                style={{ minWidth: 0 }}
                type={TextfieldType.IMASK_NUMBER}
                onChange={handleCodeChange}
            />
            <Textfield
                clearable
                transparent
                defaultValue={number}
                disabled={disabled}
                imaskOptions={imaskOptions}
                key={numberMask}
                mask={numberMask}
                onFocus={(e) => e.currentTarget.select()}
                placeholder={numberMask}
                ref={numberRef}
                rootClassName={css.numberRoot}
                style={{ minWidth: 0 }}
                type={TextfieldType.IMASK_NUMBER}
                onChange={handleNumberChange}
            />
        </div>
    );
};
PhoneInput.displayName = 'PhoneInput';
